import type React from "react";
import { useEffect, useRef } from "react";
import {
  calculateTotalGanttHeight,
  SPEED_BAR_BOTTOM_INSET,
  xCoordinateToDate,
} from "../../helpers/bar-helper";
import {
  getGanttBackgroundHit,
  getGanttSvgPoint,
  isGanttBackgroundTarget,
  nearestRenderedDelay,
  nearestStrandLossSegment,
} from "../../helpers/gantt-context-hit";
import type { StrandLossOverlaySegment } from "../../helpers/mapStrandLossOverlays";
import type { GanttProps } from "../../types/public-types";
import { Calendar, type CalendarProps } from "../calendar/calendar";
import { Grid, type GridProps } from "../grid/grid";
import {
  AssetGanttContent,
  type AssetGanttContentProps,
} from "./asset-gantt-content";
import styles from "./gantt.module.css";

export type AssetGanttProps = {
  gridProps: GridProps;
  calendarProps: CalendarProps;
  barProps: AssetGanttContentProps;
  ganttHeight: number;
  scrollX: number;
  scrollY: number;
  barFill: number;
  lineSectionFill: number;
  onBackgroundContextMenu?: GanttProps["onBackgroundContextMenu"];
  strandLossSegments: StrandLossOverlaySegment[];
};

export const AssetGantt: React.FunctionComponent<AssetGanttProps> = ({
  gridProps,
  calendarProps,
  barProps,
  ganttHeight,
  scrollX,
  scrollY,
  barFill,
  lineSectionFill,
  onBackgroundContextMenu,
  strandLossSegments,
}) => {
  const ganttSVGRef = useRef<SVGSVGElement>(null);
  const horizontalContainerRef = useRef<HTMLDivElement>(null);
  const verticalGanttContainerRef = useRef<HTMLDivElement>(null);
  const newBarProps = { ...barProps, svg: ganttSVGRef };

  const viewBoxCalendarProp =
    "0 0 " + gridProps.svgWidth + " " + calendarProps.headerHeight;
  const viewBoxGridProp =
    "0 0 " +
    gridProps.svgWidth +
    " " +
    calculateTotalGanttHeight(barProps.assets, barProps.rowHeight);
  useEffect(() => {
    if (horizontalContainerRef.current) {
      horizontalContainerRef.current.scrollTop = scrollY;
    }
  }, [scrollY]);

  useEffect(() => {
    if (verticalGanttContainerRef.current) {
      verticalGanttContainerRef.current.scrollLeft = scrollX;
    }
  }, [scrollX]);
  return (
    <div
      className={styles.ganttVerticalContainer}
      ref={verticalGanttContainerRef}
      style={{ width: gridProps.svgWidth }}
      dir="ltr"
      onContextMenu={(e) => {
        e.preventDefault();
        const svg = ganttSVGRef.current;
        if (
          !onBackgroundContextMenu ||
          !svg ||
          !isGanttBackgroundTarget(e.target as Element, svg)
        )
          return;
        const svgPoint = getGanttSvgPoint(svg, e.clientX, e.clientY);
        if (!svgPoint) return;
        const hit = getGanttBackgroundHit(
          barProps.assets,
          svgPoint,
          gridProps.svgWidth,
          barProps.rowHeight,
          barFill,
          lineSectionFill,
          SPEED_BAR_BOTTOM_INSET,
          strandLossSegments,
        );
        if (!hit) return;
        onBackgroundContextMenu(e, {
          ...hit,
          nearestLoss:
            hit.zone === "strand" && hit.asset.name === "Caster"
              ? nearestStrandLossSegment(strandLossSegments, hit.x, hit.y)?.loss
              : undefined,
          nearestDelay:
            hit.zone === "delay" &&
            (hit.asset.name === "Caster" || hit.asset.name === "EAF")
              ? (nearestRenderedDelay(hit.asset, hit.x, hit.y) ?? undefined)
              : undefined,
          timestamp: xCoordinateToDate(
            hit.x,
            calendarProps.dateSetup.dates,
            calendarProps.columnWidth,
          ),
        });
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        // width={gridProps.svgWidth}
        // height={calendarProps.headerHeight}
        fontFamily={barProps.fontFamily}
        viewBox={viewBoxCalendarProp}
      >
        <Calendar {...calendarProps} />
      </svg>
      <div
        ref={horizontalContainerRef}
        className={styles.horizontalContainer}
        style={
          ganttHeight
            ? { height: ganttHeight, width: "100%" }
            : { width: "100%" }
        }
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          // width={gridProps.svgWidth}
          // height={barProps.rowHeight * barProps.assets.length}
          fontFamily={barProps.fontFamily}
          viewBox={viewBoxGridProp}
          ref={ganttSVGRef}
        >
          <Grid {...gridProps} />
          <AssetGanttContent {...newBarProps} />
        </svg>
      </div>
    </div>
  );
};
